import axios from "axios";
import { User, InviteUserRequest, ResendInviteRequest, GroupMemberRequest, GetUsersResult, InviteUserResult, InviteUserError } from "../models/User";
import { Company, CreateCompanyRequest, CreateRequestPeriodRequest, RequestPeriod } from "../models/Company";
import { ApiResult } from "../models/ApiResult";

export type { InviteUserRequest, ResendInviteRequest, CreateCompanyRequest, CreateRequestPeriodRequest, GetUsersResult, ApiResult, GroupMemberRequest, InviteUserResult, InviteUserError };

/** Extract error message from API response — handles both plain strings and { message } objects */
function extractErrorMsg(err: any, fallback: string): string {
  const data = err?.response?.data;
  if (typeof data === "string" && data.length > 0) return data;
  if (data?.message) return data.message;
  return err?.message || fallback;
}
/**
 * Create a new company
 */
export async function createCompany(
  request: CreateCompanyRequest,
  getAccessToken: () => Promise<string>
): Promise<ApiResult<Company>> {
  try {
    const token = await getAccessToken();
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/SuperAdmin/companies`;

    // console.log("[userService] Creating company:", request);
    const res = await axios.post(apiUrl, request, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });

    return {
      ok: true,
      status: res.status,
      data: res.data
    };
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    const errorMsg = extractErrorMsg(err, "Failed to create company");
    console.error("[userService] Create company failed:", status, errorMsg);
    return {
      ok: false,
      status,
      error: errorMsg
    };
  }
}

/** * Create a request period for a company
 */
export async function createRequestPeriod(
  companyId: number,
  request: CreateRequestPeriodRequest,
  getAccessToken: () => Promise<string>
): Promise<ApiResult<RequestPeriod>> {
  try {
    const token = await getAccessToken();
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/SuperAdmin/companies/${companyId}/request-periods`;

    // console.log("[userService] Creating request period:", request);
    const res = await axios.post(apiUrl, request, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });
    
    // Response: { companyId, requestPeriod, databaseResult }
    return {
      ok: true,
      status: res.status,
      data: res.data.requestPeriod
    };
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    const errorMsg = extractErrorMsg(err, "Failed to create request period");
    console.error("[userService] Create request period failed:", status, errorMsg);
    return {
      ok: false,
      status,
      error: errorMsg
    };
  }
}

/**
 * Update a request period for a company
 */
export async function updateRequestPeriod(
  requestPeriodId: number,
  request: CreateRequestPeriodRequest,
  getAccessToken: () => Promise<string>
): Promise<ApiResult<RequestPeriod>> {
  try {
    const token = await getAccessToken();
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/SuperAdmin/request-periods/${requestPeriodId}`;

    // console.log("[userService] Updating request period:", request);
    const res = await axios.put(apiUrl, request, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });
    
    // Response: { requestPeriod, databaseResult }
    return {
      ok: true,
      status: res.status,
      data: res.data.requestPeriod
    };
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    const errorMsg = extractErrorMsg(err, "Failed to update request period");
    console.error("[userService] Update request period failed:", status, errorMsg);
    return {
      ok: false,
      status,
      error: errorMsg
    };
  }
}
/**
 * Invite a new user to Entra
 */
export async function createUser(
  user: User,
  adminUsername: string,
  groupsToAdd: string[],
  getAccessToken: () => Promise<string>,
  sendInvite: boolean = true
): Promise<ApiResult<InviteUserResult>> {
  try {
    const token = await getAccessToken();
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/entra/users/invite`;

    const inviteRequest: InviteUserRequest = {
      email: user.email || "",
      displayName: user.userName || "",
      redirectUrl: `${window.location.origin}`,
      company: user.company || "",
      adminUserId: user.adminUserId || 0,
      companyId: user.companyId || 0,
      adminUsername: adminUsername,
      groupObjectIds: groupsToAdd,
      sendInvite
    };

    const res = await axios.post<InviteUserResult>(apiUrl, inviteRequest, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });
    
    return {
      ok: true,
      status: res.status,
      data: res.data
    };
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    const data = err?.response?.data as InviteUserError | undefined;
    const errorMsg = data?.message || extractErrorMsg(err, "Failed to invite user");
    const dbDetail = data?.databaseResult;
    console.error("[userService] Invite user failed:", status, errorMsg, dbDetail);
    return {
      ok: false,
      status,
      error: dbDetail ? `${errorMsg} (DB: ${dbDetail})` : errorMsg
    };
  }
}



/**
 * Delete a user by ID
 */
export async function RemoveUserFromGroups(
  userId: number,
  getAccessToken: () => Promise<string>
): Promise<ApiResult<null>> {
  try {
    const groupId = process.env.REACT_APP_ENTRA_USER_GROUP_ID || 1;
    const token = await getAccessToken();
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/Entra/groups/${groupId}/members/${userId}`;
    
    const res = await axios.delete(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
    return {
      ok: true,
      status: res.status
    };
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    const errorMsg = extractErrorMsg(err, "Failed to delete user");
    console.error("[userService] Delete user failed:", status, errorMsg);
    return {
      ok: false,
      status,
      error: errorMsg
    };
  }
}

 

/**
 * Get companies and their request periods for a user
 */
export async function getCompanies(
  getAccessToken: () => Promise<string>
): Promise<ApiResult<Company[]>> {
  try {
    const token = await getAccessToken();
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/Entra/companies`;
    
    // console.log("[userService] Fetching companies for:");
    const res = await axios.get<Company[]>(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    
     //console.log("[userService] Fetched companies:", res.data);
    return {
      ok: true,
      status: res.status,
      data: res.data
    };
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    const errorMsg = extractErrorMsg(err, "Failed to fetch companies");
    console.error("[userService] Get companies failed:", status, errorMsg);
    return {
      ok: false,
      status,
      error: errorMsg
    };
  }
}

/**
 * Get all request periods across all companies
 */
export function getAllRequestPeriods(companies: Company[]) {
  return companies.flatMap(c => c.requestPeriod || []);
}

/**
 * Get active request periods (current time is between start and end)
 */
export function getActiveRequestPeriods(companies: Company[]) {
  const now = new Date().getTime();
  return getAllRequestPeriods(companies).filter(period => {
    const start = new Date(period.startTime).getTime();
    const end = new Date(period.endTime).getTime();
    return now >= start && now <= end;
  });
}

/**
 * Get upcoming request periods (start time is in the future)
 */
export function getUpcomingRequestPeriods(companies: Company[]) {
  const now = new Date().getTime();
  return getAllRequestPeriods(companies).filter(period => {
    const start = new Date(period.startTime).getTime();
    return start > now;
  });
}

/**
 * Get past request periods (end time has passed)
 */
export function getPastRequestPeriods(companies: Company[]) {
  const now = new Date().getTime();
  return getAllRequestPeriods(companies).filter(period => {
    const end = new Date(period.endTime).getTime();
    return end < now;
  });
}
export interface GroupMemberResponse {
  message: string;
  userId: string;
  groupId: string;
  entraAdmin: boolean;
  entraUser: boolean;
  entraReader: boolean;
}

/**
 * Add a user to an Entra group
 */
export async function addUserToGroup(
  request: GroupMemberRequest,
  getAccessToken: () => Promise<string>
): Promise<{ ok: boolean; status: number; data?: GroupMemberResponse; error?: string }> {
  try {
    const token = await getAccessToken();
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/entra/groups/members`;
    const res = await axios.post<GroupMemberResponse>(apiUrl, request, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });
    return { ok: true, status: res.status, data: res.data };
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    const errorMsg = extractErrorMsg(err, "Failed to add user to group");
    console.error("[userService] Add user to group failed:", status, errorMsg);
    return { ok: false, status, error: errorMsg };
  }
}

/**
 * Remove a user from an Entra group
 */
export async function removeUserFromGroup(
  request: GroupMemberRequest,
  getAccessToken: () => Promise<string>
): Promise<{ ok: boolean; status: number; data?: GroupMemberResponse; error?: string }> {
  try {
    const token = await getAccessToken();
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/entra/groups/members`;
    const res = await axios.delete<GroupMemberResponse>(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      data: request
    });
    return { ok: true, status: res.status, data: res.data };
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    const errorMsg = extractErrorMsg(err, "Failed to remove user from group");
    console.error("[userService] Remove user from group failed:", status, errorMsg);
    return { ok: false, status, error: errorMsg };
  }
}

/**
 * Refresh a single company's data (users, roles, etc.)
 */
export async function refreshCompany(
  companyId: number,
  getAccessToken: () => Promise<string>
): Promise<ApiResult<Company>> {
  try {
    const token = await getAccessToken();
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/entra/companies/${companyId}/refresh`;

    const res = await axios.get<Company>(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return {
      ok: true,
      status: res.status,
      data: res.data
    };
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    const errorMsg = extractErrorMsg(err, "Failed to refresh company");
    console.error("[userService] Refresh company failed:", status, errorMsg);
    return {
      ok: false,
      status,
      error: errorMsg
    };
  }
}

export interface SyncCompanyRolesResponse {
  company: Company;
  usersUpdated: number;
  usersFailed: string[];
}

/**
 * Re-fetch every user's role and display name from Entra (the source of truth) and
 * persist them to the database, correcting any drift — e.g. from role changes made
 * directly in the Entra portal, or from historical bugs. This is what downstream
 * systems like Power BI read the role from.
 */
export async function syncCompanyRoles(
  companyId: number,
  getAccessToken: () => Promise<string>
): Promise<ApiResult<SyncCompanyRolesResponse>> {
  try {
    const token = await getAccessToken();
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/entra/companies/${companyId}/sync-roles`;

    const res = await axios.post<SyncCompanyRolesResponse>(apiUrl, null, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return {
      ok: true,
      status: res.status,
      data: res.data
    };
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    const errorMsg = extractErrorMsg(err, "Failed to sync company roles");
    console.error("[userService] Sync company roles failed:", status, errorMsg);
    return {
      ok: false,
      status,
      error: errorMsg
    };
  }
}

/**
 * Resend an existing user invite in Entra
 */
export async function resendInvite(
  request: ResendInviteRequest,
  getAccessToken: () => Promise<string>
): Promise<ApiResult<null>> {
  try {
    const token = await getAccessToken();
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api/entra/users/reinvite`;

    const res = await axios.post(apiUrl, request, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    });

    return {
      ok: true,
      status: res.status
    };
  } catch (err: any) {
    const status = err?.response?.status ?? 0;
    const errorMsg = extractErrorMsg(err, "Failed to resend invite");
    console.error("[userService] Resend invite failed:", status, errorMsg);
    return {
      ok: false,
      status,
      error: errorMsg
    };
  }
}